import { compileAmbient, hasCompileAmbient } from './compile-ambient';
import { encounterField } from '../../shared/array-types';

type Row = Record<string, unknown>;
type InventoryBaseBridge = (id: string, announce?: boolean) => boolean;
type BridgeWindow = Window & { FateTide?: { encounterCache?: { has?: (id: string) => boolean } };
    ftRequireInventoryBase?: InventoryBaseBridge; ftBaseCommitsPaused?: () => boolean };

/**
 * The page runtime that owns this encounter's floor. A framed FT3D scene loads its own pre-template runtime, which adopts
 * the encounter only once its own encounter read lands; `own ?? parent` let that runtime answer for a row it does not
 * fill. The parent page that caches the encounter row — the row the scene's perception reads — owns it; a standalone
 * page asks its own runtime (playtest 2026-10-13).
 */
function inventoryBaseOwner(id: string): BridgeWindow {
    try {
        const parent = (compileAmbient() as BridgeWindow).parent as BridgeWindow | undefined;
        const cache = parent && parent !== compileAmbient() ? parent.FateTide?.encounterCache : undefined;
        if (parent?.ftRequireInventoryBase && [id, id.toUpperCase(), id.toLowerCase()].some(key => cache?.has?.(key))) return parent;
    } catch { /* A cross-origin parent: the scene's own runtime decides. */ }
    return compileAmbient() as BridgeWindow;
}
function inventoryBaseBridge(id: string): InventoryBaseBridge | undefined {
    return inventoryBaseOwner(id).ftRequireInventoryBase;
}

/** Whether the owning runtime holds this id's floor un-refused; null when this page has no inventory runtime. */
export function inventoryBaseReady(id: string): boolean | null {
    if (hasCompileAmbient() || !id) return null;
    try {
        const bridge = inventoryBaseBridge(id);
        return bridge ? bridge(id, false) : null;
    } catch {
        return null; // An unreachable runtime decides nothing; the row's own refusal still stands.
    }
}

/**
 * Why this floor cannot be read, or null. A paused runtime (a newer engine was served — every pre-template deploy) needs a
 * page reload, not a port: naming it keeps a deploy from reading as "inventory not compiled" (playtest 2026-10-13).
 */
function inventoryFloorOwnRefusal(encounter: Row): 'engine-version-reload' | 'inventory-base-unavailable' | null {
    const id = String(encounterField(encounter as never, 'encounter_id') ?? '');
    const pending = typeof encounter._inventory_refusal === 'string'
        || encounter.encounter_inventories_arr === null;
    if (inventoryBaseReady(id) !== false && !pending) return null;
    try { if (!hasCompileAmbient() && id && inventoryBaseOwner(id).ftBaseCommitsPaused?.()) return 'engine-version-reload'; }
    catch { /* An unreachable runtime decides nothing. */ }
    return 'inventory-base-unavailable';
}

/** A visible placeholder is enough to render; rules requiring its floor semantics must refuse by name. */
export function inventoryFloorRefusal(encounter: Row): string | null {
    const own = inventoryFloorOwnRefusal(encounter);
    if (own) return own;
    for (const row of Array.isArray(encounter.encounter_inventories_arr) ? encounter.encounter_inventories_arr : [])
        if (row && typeof row === 'object' && typeof row._inventory_refusal === 'string') return row._inventory_refusal;
    return null;
}

/** An absent/refused inventory facet is never evidence of an empty floor. */
export function inventoryFloorAvailable(encounter: Row): boolean {
    return inventoryFloorOwnRefusal(encounter) === null;
}

export function requireInventoryFloor(encounter: Row): void {
    const refusal = inventoryFloorRefusal(encounter);
    if (refusal) throw new Error(refusal.startsWith('unported:') ? refusal : `unported:${refusal}`);
}
